import { controlApiFetch, controlApiUrl } from "@/transport/control-api"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"

/** The account Mux Oracle's current conversation and the machine running its turns ("" while none holds the lease). */
export type AccountMuxOracleState = {
  conversationID: string
  revision: number
  title: string
  /** Unix seconds. */
  updatedAt: number
  lastMachine: string
  runner: string
  /** Seconds the runner's lease had left when the account answered, by its clock; 0 without a lease. */
  leaseRemaining: number
}

/** The machine `POST /api/mux-oracle/run` woke, or that no machine is online to run the Oracle. */
export type MuxOracleRun = { machine: string } | { offline: true }

export type McpToken = { id: string; name: string; /** Unix seconds. */ createdAt: number }

/** A token just created: the only response that carries its secret. */
export type CreatedMcpToken = McpToken & { token: string }

const invalid = "Server returned an invalid Kettle Oracle response"

function decodeState(value: unknown): AccountMuxOracleState {
  const state = jsonObject(value, invalid)
  return {
    conversationID: jsonString(state.conversationID ?? "", invalid),
    revision: jsonNumber(state.revision ?? 0, invalid),
    title: jsonString(state.title ?? "", invalid),
    updatedAt: jsonNumber(state.updatedAt ?? 0, invalid),
    lastMachine: jsonString(state.lastMachine ?? "", invalid),
    runner: jsonString(state.runner ?? "", invalid),
    // Measured against the account's own clock, so a device's clock that runs
    // ahead or behind cannot move the refresh at the lease's expiry.
    leaseRemaining: Math.max(0, jsonNumber(state.leaseExpiresAt ?? 0, invalid) - jsonNumber(state.now ?? 0, invalid)),
  }
}

function decodeToken(value: unknown): McpToken {
  const token = jsonObject(value, invalid)
  return { id: jsonString(token.id, invalid), name: jsonString(token.name, invalid), createdAt: jsonNumber(token.createdAt, invalid) }
}

export async function getAccountMuxOracle(signal?: AbortSignal) {
  return expectJson(await controlApiFetch("/api/mux-oracle", { cache: "no-store", signal }), decodeState)
}

/** Picks the runner and wakes it; a 409 means no machine is online to run it. */
export async function runAccountMuxOracle(signal?: AbortSignal): Promise<MuxOracleRun> {
  const response = await controlApiFetch("/api/mux-oracle/run", { method: "POST", signal })
  if (response.status === 409) return { offline: true }
  return expectJson(response, (value) => ({ machine: jsonString(jsonObject(value, invalid).machine, invalid) }))
}

export async function listMcpTokens(signal?: AbortSignal) {
  return expectJson(
    await controlApiFetch("/api/mux-oracle/mcp-tokens", { cache: "no-store", signal }),
    (value) => jsonArray(jsonObject(value, invalid).tokens ?? [], invalid).map(decodeToken),
  )
}

export async function createMcpToken(name: string): Promise<CreatedMcpToken> {
  const response = await controlApiFetch("/api/mux-oracle/mcp-tokens", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  })
  return expectJson(response, (value) => ({ ...decodeToken(value), token: jsonString(jsonObject(value, invalid).token, invalid) }), {
    error: "Could not create the MCP token",
  })
}

export async function revokeMcpToken(id: string) {
  await expectResponse(await controlApiFetch(`/api/mux-oracle/mcp-tokens/${encodeURIComponent(id)}`, { method: "DELETE" }), {
    error: "Could not revoke the MCP token",
  })
}

/** Where MCP clients reach the account Mux Oracle. */
export const muxOracleMcpUrl = () => controlApiUrl("/mcp")
